import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, fmtEur, today, waLink } from "@/lib/api";
import { useCentro } from "@/lib/centro";
import { PageTitle } from "@/components/Badges";
import { DateField } from "@/components/Fields";
import { RespuestasTto } from "@/components/RespuestasTtoStats";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const PRESETS = [
  { id: "mes", label: "Este mes", r: (n) => [iso(new Date(n.getFullYear(), n.getMonth(), 1)), iso(new Date(n.getFullYear(), n.getMonth() + 1, 0))] },
  { id: "mes_pasado", label: "Mes pasado", r: (n) => [iso(new Date(n.getFullYear(), n.getMonth() - 1, 1)), iso(new Date(n.getFullYear(), n.getMonth(), 0))] },
  { id: "trimestre", label: "Últimos 3 meses", r: (n) => [iso(new Date(n.getFullYear(), n.getMonth() - 2, 1)), iso(new Date(n.getFullYear(), n.getMonth() + 1, 0))] },
  { id: "anio", label: "Este año", r: (n) => [`${n.getFullYear()}-01-01`, `${n.getFullYear()}-12-31`] },
];

const MetricCard = ({ m, active, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`stat-${m.id}`}
    className={`lift text-left rounded-lg border px-4 py-3 transition-colors ${active ? "bg-[#1E3A8A] border-[#1E3A8A] text-white" : "bg-white border-slate-200 hover:border-slate-400"}`}>
    <div className={`font-[JetBrains_Mono] text-2xl font-semibold ${active ? "text-white" : m.n ? "text-slate-900" : "text-slate-300"}`}>{m.n}</div>
    <div className={`text-[11px] leading-tight ${active ? "text-blue-100" : "text-slate-600"}`}>{m.label}</div>
  </button>
);

const SIGUIO = " (luego continuó)";
const Tasa = ({ label, list, testId }) => {
  const ok = list.filter((it) => it.detalle.endsWith(SIGUIO)).length;
  return (
    <div data-testid={testId} className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
      <div className="font-[JetBrains_Mono] text-xl font-semibold text-emerald-800">{list.length ? Math.round((ok * 100) / list.length) : 0}%</div>
      <div className="text-[11px] text-emerald-900">{label}: {ok} de {list.length} acabaron continuando</div>
    </div>
  );
};

const piensan = (l) => l.filter((it) => it.detalle.toLowerCase().startsWith("lo piensan"));

const PorGrupo = ({ items, campo, vacio, titulo, testId }) => {
  const keys = [...new Set(items.map((it) => it[campo] || vacio))].sort();
  if (keys.length < 2) return null;
  return (
    <div className="mb-3" data-testid={testId}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{titulo}</div>
      <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {keys.map((c) => {
          const l = items.filter((it) => (it[campo] || vacio) === c);
          return (
            <div key={c} className="rounded-md border border-slate-200 p-2" data-testid={`${testId}-${c.toLowerCase().replace(/ /g, "-")}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">{c}</div>
              <div className="grid gap-1">
                <Tasa label="Lo pensaban" list={piensan(l)} />
                <Tasa label="Todos" list={l} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Recuperados = ({ items }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
      <Tasa label="Lo pensaban" list={piensan(items)} testId="stat-recuperados-piensan" />
      <Tasa label="Todos los motivos" list={items} testId="stat-recuperados-total" />
    </div>
    <PorGrupo items={items} campo="centro" vacio="Sin centro" titulo="Por centro" testId="stat-recuperados-centros" />
    <PorGrupo items={items} campo="opto" vacio="Sin optometrista" titulo="Por optometrista" testId="stat-recuperados-optos" />
  </>
);

const Detalle = ({ m }) => (
  <div data-testid="stat-detalle" className="bg-white border border-slate-200 rounded-lg p-4 mt-3 fade-up">
    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">{m.label} · {m.n}</h3>
    {m.n === 0 && <p className="text-sm text-slate-400">Nada en estas fechas.</p>}
    {m.id === "no_siguen" && m.n > 0 && <Recuperados items={m.items} />}
    {m.id === "no_siguen" && m.n > 0 && (
      <div className="flex flex-wrap gap-2 mb-3" data-testid="stat-motivos">
        {Object.entries(m.items.reduce((a, it) => ({ ...a, [it.detalle || "Sin motivo"]: (a[it.detalle || "Sin motivo"] || 0) + 1 }), {})).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
          <span key={k} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-700">{k}: <b>{n}</b></span>
        ))}
      </div>
    )}
    <div className="divide-y divide-slate-100 max-h-[360px] overflow-y-auto">
      {m.items.map((it, i) => (
        <Link key={i} to={it.link} data-testid={`stat-item-${i}`} className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 py-2 text-sm hover:bg-slate-50 -mx-2 px-2 rounded">
          <span className="font-[JetBrains_Mono] text-xs text-slate-500 w-20">{fmtDate(it.fecha)}</span>
          <span className="flex-1 truncate text-slate-800">{it.nombre}</span>
          <span className="text-[11px] text-slate-500 uppercase w-24">{it.centro}</span>
          <span className="text-[11px] text-slate-500 truncate sm:max-w-[200px]">{it.detalle}</span>
        </Link>
      ))}
    </div>
  </div>
);

const pct = (a, b) => (b ? Math.round((a * 100) / b) : 0);

const ConversionOptos = ({ rows }) => (
  <div className="mb-6 fade-up" data-testid="stat-conversion-optos">
    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Presupuestos por optometrista · aprobados</h3>
    {rows.length === 0 ? <p className="text-sm text-slate-400">No hay presupuestos en estas fechas.</p> : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {rows.map((r) => (
          <div key={r.opto} className="bg-white border border-slate-200 rounded-lg px-4 py-3" data-testid={`conv-${r.opto.toLowerCase().replace(/ /g, "-")}`}>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">{r.opto}</div>
            <div className="font-[JetBrains_Mono] text-2xl font-semibold text-[#1E3A8A]">{pct(r.aprobados, r.total)}%</div>
            <div className="text-[11px] text-slate-600">{r.aprobados} aprobados de {r.total} presupuestos</div>
            {r.importe_aprobado != null && <div className="mt-1 font-[JetBrains_Mono] text-sm font-semibold text-emerald-700" data-testid={`conv-importe-${r.opto.toLowerCase().replace(/ /g, "-")}`}>{fmtEur(r.importe_aprobado)} aprobados{r.aprobados > 0 && <span className="block text-[11px] font-normal text-slate-600" data-testid={`conv-medio-${r.opto.toLowerCase().replace(/ /g, "-")}`}>media {fmtEur(r.importe_aprobado / r.aprobados)} por presupuesto</span>}</div>}
            <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden flex">
              <span className="bg-emerald-500" style={{ width: `${pct(r.aprobados, r.total)}%` }} />
              <span className="bg-amber-400" style={{ width: `${pct(r.espera, r.total)}%` }} />
              <span className="bg-rose-400" style={{ width: `${pct(r.rechazados, r.total)}%` }} />
            </div>
            <div className="mt-1 text-[11px] text-slate-500">{r.espera} esperando respuesta · {r.rechazados} rechazados</div>
            {r.conceptos?.length > 0 && (
              <div className="mt-2 border-t border-slate-100 pt-2" data-testid={`conv-conceptos-${r.opto.toLowerCase().replace(/ /g, "-")}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Qué llevan los aprobados</div>
                {r.conceptos.map((c) => (
                  <div key={c.concepto} className="flex items-center gap-2 text-[11px] text-slate-700">
                    <span className="flex-1 truncate" title={c.concepto}>{c.concepto}</span>
                    <span className="font-[JetBrains_Mono] font-semibold">{c.n}</span>
                    <span className="w-9 text-right text-slate-400">{pct(c.n, r.aprobados)}%</span>
                    {c.euros != null && <span className="w-16 text-right font-[JetBrains_Mono] text-emerald-700">{fmtEur(c.euros)}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);

const EurosTratamiento = ({ rows }) => {
  const max = Math.max(...rows.map((r) => r.euros), 1);
  const total = rows.reduce((s, r) => s + r.euros, 0);
  return (
    <div className="mb-6 fade-up" data-testid="stat-euros-tratamiento">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Euros aprobados por tratamiento · {fmtEur(total)}</h3>
      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2 lg:w-2/3">
        {rows.map((r, i) => (
          <div key={r.concepto} className="grid grid-cols-[minmax(0,14rem)_1fr_6rem_3rem] items-center gap-3 text-xs" data-testid={`euros-trat-${i}`}>
            <span className="truncate text-slate-800" title={r.concepto}>{r.concepto}</span>
            <span className="h-2 rounded-full bg-slate-100 overflow-hidden"><span className="block h-full bg-emerald-500" style={{ width: `${(r.euros * 100) / max}%` }} /></span>
            <span className="text-right font-[JetBrains_Mono] font-semibold text-emerald-700">{fmtEur(r.euros)}</span>
            <span className="text-right text-slate-400">{pct(r.euros, total)}%</span>
          </div>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-slate-400">Con los descuentos de cada presupuesto repartidos entre sus líneas.</p>
    </div>
  );
};

const MES_TXT = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const mesLbl = (m) => `${MES_TXT[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;

const EurosMes = ({ meses, tratamientos }) => {
  const nombres = tratamientos.map((t) => t.concepto);
  const max = Math.max(...meses.flatMap((m) => Object.values(m.conceptos)), 1);
  const totMes = (m) => Object.values(m.conceptos).reduce((s, x) => s + x, 0);
  const maxMes = Math.max(...meses.map(totMes), 1);
  return (
    <div className="mb-6 fade-up" data-testid="stat-euros-mes">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Euros aprobados mes a mes</h3>
      <div className="bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto">
        <table className="text-xs border-separate border-spacing-1">
          <thead><tr><th className="text-left font-medium text-slate-500 pr-3">Tratamiento</th>{meses.map((m) => <th key={m.mes} className="font-medium text-slate-500 px-1 whitespace-nowrap">{mesLbl(m.mes)}</th>)}</tr></thead>
          <tbody>
            {nombres.map((n, i) => (
              <tr key={n} data-testid={`euros-mes-row-${i}`}>
                <td className="pr-3 text-slate-800 max-w-[14rem] truncate" title={n}>{n}</td>
                {meses.map((m) => {
                  const v = m.conceptos[n] || 0;
                  return <td key={m.mes} className="rounded px-2 py-1 text-right font-[JetBrains_Mono] whitespace-nowrap" style={{ backgroundColor: v ? `rgba(16,185,129,${0.12 + (0.75 * v) / max})` : "#f8fafc", color: v / max > 0.6 ? "white" : "#334155" }}>{v ? fmtEur(v) : "·"}</td>;
                })}
              </tr>
            ))}
            <tr data-testid="euros-mes-total">
              <td className="pr-3 pt-2 font-semibold text-slate-700">Total del mes</td>
              {meses.map((m) => (
                <td key={m.mes} className="pt-2 align-bottom">
                  <div className="mx-auto w-full rounded-t bg-[#1E3A8A]" style={{ height: `${Math.max(4, (totMes(m) * 48) / maxMes)}px` }} />
                  <div className="text-center font-[JetBrains_Mono] font-semibold text-slate-800 whitespace-nowrap">{fmtEur(totMes(m))}</div>
                </td>
              ))}
            </tr>
            <tr data-testid="euros-mes-anterior">
              <td className="pr-3 text-slate-500">Mismo mes año anterior</td>
              {meses.map((m) => <td key={m.mes} className="text-center font-[JetBrains_Mono] text-slate-500 whitespace-nowrap">{fmtEur(m.anterior || 0)}</td>)}
            </tr>
            <tr data-testid="euros-mes-variacion">
              <td className="pr-3 text-slate-500">Variación</td>
              {meses.map((m) => {
                const a = m.anterior || 0;
                const t = totMes(m);
                const v = a ? Math.round(((t - a) * 100) / a) : null;
                return (
                  <td key={m.mes} className={`text-center font-[JetBrains_Mono] font-semibold whitespace-nowrap ${v == null ? "text-slate-300" : v >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                    {v == null ? (t ? "nuevo" : "·") : `${v >= 0 ? "▲ +" : "▼ "}${v}%`}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-1 text-[11px] text-slate-400">Por mes del presupuesto. Elige «Este año» o un rango largo arriba para ver la temporada.</p>
    </div>
  );
};

const PrevBox = ({ label, v, cls, testId }) => (
  <div className={`rounded-lg border px-4 py-3 ${cls}`} data-testid={testId}>
    <div className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
    <div className="font-[JetBrains_Mono] text-2xl font-semibold">{fmtEur(v.euros)}</div>
    <div className="text-[11px] opacity-80">{v.n} presupuesto{v.n === 1 ? "" : "s"}</div>
  </div>
);

const recuperarMsg = (it) => `Hola, os escribimos de Salud y Visión. Hace un tiempo os enviamos el presupuesto del tratamiento para ${it.nombre}. ¿Habéis podido pensarlo? Si tenéis cualquier duda o queréis que lo revisemos juntos, estaremos encantados de ayudaros. Gracias.`;

const ItemEdad = ({ it, i }) => {
  const [escrito, setEscrito] = useState(it.escrito);
  const marcar = async (v) => {
    await api.post(`/presupuestos/${it.id}/recuperar-escrito`, { escrito: v });
    setEscrito(v ? today() : "");
  };
  return (
    <div className={`flex flex-wrap items-center gap-3 py-2 text-sm ${escrito ? "opacity-60" : ""}`} data-testid={`prev-item-${i}`}>
      <Link to={`/pacientes/${it.patient_id}`} className="font-medium text-slate-900 hover:underline">{it.nombre}</Link>
      <span className="text-[11px] text-slate-500">{it.centro} · {it.estado === "DUDOSO" ? "dudoso" : "enviado"} el {fmtDate(it.fecha_envio)} ({it.dias} días)</span>
      <span className="ml-auto font-[JetBrains_Mono] text-xs font-semibold text-slate-800">{fmtEur(it.total)}</span>
      <label className="flex items-center gap-1 text-[11px] text-slate-600 whitespace-nowrap">
        <input type="checkbox" checked={!!escrito} onChange={(e) => marcar(e.target.checked)} data-testid={`prev-escrito-${i}`} />
        {escrito ? `Escrito ${fmtDate(escrito)}` : "Ya escrito"}
      </label>
      {it.telefono ? <a href={waLink(it.telefono, recuperarMsg(it))} target="_blank" rel="noreferrer" onClick={() => !escrito && marcar(true)} data-testid={`prev-wa-${i}`}
        className={`rounded px-2 py-1 text-xs font-medium text-white hover:opacity-90 ${escrito ? "bg-slate-400" : "bg-[#25D366]"}`}>WhatsApp</a>
        : <span className="text-[11px] text-slate-400">sin teléfono</span>}
    </div>
  );
};

const ListaEdad = ({ items, label, onClose }) => (
  <div className="mt-3 border-t border-slate-100 pt-3" data-testid="prev-lista">
    <div className="flex items-center mb-1"><span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">{label} · {items.length} · {items.filter((x) => !x.escrito).length} sin escribir</span>
      <button onClick={onClose} data-testid="prev-lista-cerrar" className="ml-auto text-[11px] text-slate-400 hover:text-slate-700">cerrar</button></div>
    <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
      {items.map((it, i) => <ItemEdad key={it.id} it={it} i={i} />)}
    </div>
  </div>
);

const Prevision = ({ p }) => {
  const [sel, setSel] = useState(null);
  const { ENVIADO: env, DUDOSO: dud, HECHO: hecho } = p.estados;
  const espera = env.euros + dud.euros;
  const EDAD = [["<30", "Enviados hace menos de 30 días", "bg-emerald-500"], ["30-90", "Entre 30 y 90 días", "bg-amber-400"], [">90", "Más de 90 días", "bg-rose-400"]];
  return (
    <div className="mb-6 fade-up" data-testid="stat-prevision">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Previsión: presupuestos pendientes de respuesta (hoy)</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
        <PrevBox label="Enviados" v={env} cls="bg-white border-slate-200 text-[#1E3A8A]" testId="prev-enviados" />
        <PrevBox label="Dudosos" v={dud} cls="bg-white border-slate-200 text-amber-700" testId="prev-dudosos" />
        <PrevBox label="Hechos sin enviar" v={hecho} cls="bg-slate-50 border-slate-200 text-slate-600" testId="prev-hechos" />
      </div>
      {espera > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="prev-edades">
          <div className="text-[11px] text-slate-600 mb-2">{fmtEur(espera)} en enviados y dudosos, según cuánto llevan esperando:</div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden flex mb-2">
            {EDAD.map(([k, , c]) => <button key={k} type="button" onClick={() => setSel(sel === k ? null : k)} data-testid={`prev-barra-${k}`} title="Ver la lista" className={`${c} hover:opacity-80 ${sel === k ? "ring-2 ring-slate-700 ring-inset" : ""}`} style={{ width: `${pct(p.edades[k], espera)}%` }} />)}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-slate-600">
            {EDAD.map(([k, l, c]) => <button key={k} type="button" onClick={() => setSel(sel === k ? null : k)} data-testid={`prev-leyenda-${k}`} className={`flex items-center gap-1.5 hover:underline ${sel === k ? "font-semibold text-slate-900" : ""}`}><span className={`h-2 w-2 rounded-full ${c}`} />{l}: <b className="font-[JetBrains_Mono]">{fmtEur(p.edades[k])}</b></button>)}
          </div>
          {sel ? <ListaEdad items={p.edad_items?.[sel] || []} label={EDAD.find((e) => e[0] === sel)[1]} onClose={() => setSel(null)} />
            : <p className="mt-2 text-[11px] text-slate-400">Pulsa un color para ver sus presupuestos. Los más recientes son los que tienen más probabilidad de entrar en los próximos meses.</p>}
        </div>
      )}
    </div>
  );
};

export default function Estadisticas() {
  const { centro } = useCentro();
  const [[desde, hasta], setRango] = useState(PRESETS[0].r(new Date()));
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    if (!desde || !hasta) return;
    api.get("/estadisticas", { params: { desde, hasta, centro } }).then((r) => setD(r.data));
  }, [desde, hasta, centro]);
  const all = d ? d.grupos.flatMap((g) => g.metricas) : [];
  const m = all.find((x) => x.id === sel);
  return (
    <div data-testid="estadisticas-page">
      <PageTitle title="Estadísticas" subtitle={`${centro ? `Centro ${centro}` : "Todos los centros"} · ${fmtDate(desde)} – ${fmtDate(hasta)}`} />
      <div className="flex flex-wrap items-end gap-3 mb-6">
        {PRESETS.map((p) => (
          <button key={p.id} data-testid={`preset-${p.id}`} onClick={() => setRango(p.r(new Date()))}
            className="rounded-full border px-3 py-1.5 text-xs font-medium bg-white text-slate-600 border-slate-200 hover:border-slate-400 transition-colors">{p.label}</button>
        ))}
        <div className="w-40"><DateField label="Desde" value={desde} onChange={(v) => setRango([v, hasta])} testId="stat-desde" /></div>
        <div className="w-40"><DateField label="Hasta" value={hasta} onChange={(v) => setRango([desde, v || today()])} testId="stat-hasta" /></div>
        {d?.cobrado != null && <div className="ml-auto bg-white border border-slate-200 rounded-lg px-4 py-2" data-testid="stat-cobrado">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Cobrado en el periodo</div>
          <div className="font-[JetBrains_Mono] text-lg font-semibold text-emerald-700">{fmtEur(d.cobrado)}</div>
        </div>}
      </div>
      {!d && <p className="text-sm text-slate-400" data-testid="stat-loading">Cargando…</p>}
      {d?.grupos.map((g) => (
        <div key={g.grupo} className="mb-6 fade-up" data-testid={`stat-grupo-${g.grupo}`}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">{g.grupo}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {g.metricas.map((x) => <MetricCard key={x.id} m={x} active={sel === x.id} onClick={() => setSel(sel === x.id ? null : x.id)} />)}
          </div>
          {m && g.metricas.some((x) => x.id === m.id) && <Detalle m={m} />}
        </div>
      ))}
      {d && <ConversionOptos rows={d.conversion_optos || []} />}
      {d?.respuestas_tto?.length > 0 && <RespuestasTto rows={d.respuestas_tto} />}
      {d?.prevision && <Prevision p={d.prevision} />}
      {d?.euros_tratamiento?.length > 0 && <EurosTratamiento rows={d.euros_tratamiento} />}
      {d?.euros_mes?.length > 0 && <EurosMes meses={d.euros_mes} tratamientos={d.euros_tratamiento} />}
    </div>
  );
}
